/**
 * Text Shatter Effect - 文字粉碎重组效果 (#494)
 * 
 * 2026 创意动画趋势：文字粉碎成粒子再重新组合
 * 
 * 技术要点：
 * - 使用 Canvas 绘制文字并提取像素
 * - 粒子爆炸 → 漂浮 → 重组动画
 * - Spring 动画实现流畅过渡
 * 
 * 性能优化：
 * - 采样密度控制粒子数量
 * - 使用 useMemo 避免重复计算
 * - 预计算粒子随机属性避免每帧重算
 */

import React, { useEffect, useMemo, useState } from 'react';
import {
  AbsoluteFill,
  useCurrentFrame,
  useVideoConfig,
  spring,
  interpolate,
  random,
} from 'remotion';

const COLORS = [
  '#FF6B6B', '#4ECDC4', '#45B7D1', '#96CEB4',
  '#FFEAA7', '#DDA0DD', '#FF8C94', '#A8E6CF',
  '#FFD93D', '#6C5CE7',
];

interface Particle {
  id: number;
  x: number;
  y: number;
  size: number;
  targetX: number;
  targetY: number;
  delay: number;
  angle: number;
  distance: number;
  scatterColor: string;
}

const TextShatterEffect: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();
  const [particles, setParticles] = useState<Particle[]>([]);

  const appearEnd = 18;
  const shatterStart = 18;
  const shatterEnd = 42;
  const floatEnd = 72;
  const reassembleEnd = 108;

  useEffect(() => {
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    ctx.fillStyle = 'white';
    ctx.font = 'bold 120px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('SHATTER', width / 2, height / 2);

    const imageData = ctx.getImageData(0, 0, width, height);
    const data = imageData.data;
    const tempParticles: Particle[] = [];
    let id = 0;
    const sampleGap = 4;

    for (let y = 0; y < height; y += sampleGap) {
      for (let x = 0; x < width; x += sampleGap) {
        const index = (y * width + x) * 4;
        if (data[index + 3] > 128) {
          const pid = id++;
          tempParticles.push({
            id: pid,
            x,
            y,
            size: 2 + random(`size-${pid}`) * 3,
            targetX: x,
            targetY: y,
            delay: random(`delay-${pid}`) * 8,
            angle: random(`angle-${pid}`) * Math.PI * 2,
            distance: 150 + random(`dist-${pid}`) * 250,
            scatterColor: COLORS[Math.floor(random(`clr-${pid}`) * COLORS.length)],
          });
        }
      }
    }

    setParticles(tempParticles);
  }, [width, height]);

  const animatedParticles = useMemo(() => {
    return particles.map((p) => {
      let x = p.targetX;
      let y = p.targetY;
      let opacity = 1;
      let size = p.size;
      let useColor = false;

      if (frame < shatterStart) {
        const t = interpolate(frame, [0, appearEnd], [0, 1], {
          extrapolateRight: 'clamp',
        });
        opacity = t;
        size = p.size * interpolate(t, [0, 1], [0.3, 1]);
      } else if (frame < shatterEnd) {
        const raw = interpolate(frame, [shatterStart, shatterEnd], [0, 1]);
        const t = 1 - Math.pow(1 - raw, 3);

        x = p.targetX + Math.cos(p.angle) * p.distance * t;
        y = p.targetY + Math.sin(p.angle) * p.distance * t;
        opacity = interpolate(t, [0, 1], [1, 0.75]);
        size = p.size * interpolate(t, [0, 0.4, 1], [1, 1.6, 0.8]);
        useColor = t > 0.3;
      } else if (frame < floatEnd) {
        const ft = frame - shatterEnd;
        const baseX = p.targetX + Math.cos(p.angle) * p.distance;
        const baseY = p.targetY + Math.sin(p.angle) * p.distance;

        x = baseX + Math.sin(ft * 0.1 + p.id * 0.5) * 15;
        y = baseY + Math.cos(ft * 0.08 + p.id * 0.3) * 10;
        opacity = 0.7 + Math.sin(ft * 0.15 + p.id) * 0.15;
        size = p.size * 0.8;
        useColor = true;
      } else if (frame <= reassembleEnd) {
        const relFrame = frame - floatEnd - p.delay;
        const t = spring({
          frame: Math.max(0, relFrame),
          fps,
          config: { damping: 12, stiffness: 80, mass: 0.8 },
        });

        const scatteredX = p.targetX + Math.cos(p.angle) * p.distance;
        const scatteredY = p.targetY + Math.sin(p.angle) * p.distance;

        x = interpolate(t, [0, 1], [scatteredX, p.targetX]);
        y = interpolate(t, [0, 1], [scatteredY, p.targetY]);
        opacity = interpolate(t, [0, 1], [0.7, 1]);
        size = interpolate(t, [0, 1], [p.size * 0.8, p.size]);
        useColor = t < 0.7;
      }

      return { ...p, x, y, opacity, size, useColor };
    });
  }, [particles, frame, fps]);

  const glowOpacity = interpolate(
    frame,
    [shatterStart, shatterEnd, floatEnd, reassembleEnd],
    [0, 0.6, 0.4, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  const finalGlow = interpolate(
    frame,
    [reassembleEnd - 10, reassembleEnd + 5],
    [0, 1],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' },
  );

  return (
    <AbsoluteFill
      style={{
        backgroundColor: '#0a0a0a',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        style={{
          position: 'absolute',
          width: 600,
          height: 600,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(78,205,196,0.15) 0%, rgba(255,107,107,0.08) 40%, transparent 70%)',
          left: width / 2 - 300,
          top: height / 2 - 300,
          opacity: glowOpacity,
          filter: 'blur(20px)',
        }}
      />

      <svg width={width} height={height} style={{ position: 'absolute' }}>
        {animatedParticles.map((p) => (
          <circle
            key={p.id}
            cx={p.x}
            cy={p.y}
            r={p.size}
            fill={p.useColor ? p.scatterColor : 'white'}
            opacity={p.opacity}
          />
        ))}
      </svg>

      {finalGlow > 0 && (
        <div
          style={{
            position: 'absolute',
            fontSize: 120,
            fontWeight: 'bold',
            fontFamily: 'Arial',
            color: 'white',
            opacity: finalGlow * 0.35,
            textShadow: `0 0 ${20 * finalGlow}px rgba(78,205,196,0.5), 0 0 ${40 * finalGlow}px rgba(78,205,196,0.3)`,
            pointerEvents: 'none',
          }}
        >
          SHATTER
        </div>
      )}
    </AbsoluteFill>
  );
};

export default TextShatterEffect;
